import { reactive, toRef, watchEffect } from "vue";
import { storeToRefs } from "pinia";
import useLeningdelen from "./useLeningdelen";
import { asCur, isPercentage } from "@/helpers/inputHelpers";
import {
	ltvSchema,
	maximaleHypotheekSchema,
	toegestaneLastenSchema,
	woonquoteSchema,
} from "../schemas/maximaleHypotheekSchema";
import { useDossierDataStore } from "@/store/useDossierDataStore";

export default function useMaximaleHypotheek() {
	const dossierDataStore = useDossierDataStore();
	const { data } = storeToRefs(dossierDataStore);

	const state = toRef(() => data.value.maximaleHypotheek);

	const schemas = reactive({
		maximalehypotheekSchema: maximaleHypotheekSchema,
		toegestaneLastenSchema: toegestaneLastenSchema,
		ltvSchema: ltvSchema,
		woonquoteSchema: woonquoteSchema,
	});

	const leningdelenStore = useLeningdelen();
	const { computedValues, mortgagePartsTotal } = storeToRefs(leningdelenStore);

	// Toon gewogen toetsrente
	watchEffect(() => {
		data.value.maximaleHypotheek.gewogenToetsrente = asCur(
			mortgagePartsTotal.value.toetsrente,
		);
	});

	watchEffect(() => {
		data.value.maximaleHypotheek.toetsinkomen =
			computedValues.value.toetsinkomen;
	});

	watchEffect(() => {
		data.value.maximaleHypotheek.toetsinkomenPerMaand =
			computedValues.value.toetsinkomenPerMaand;
	});

	watchEffect(() => {
		data.value.maximaleHypotheek.woonquoteBox1 = isPercentage(
			computedValues.value.woonquoteBox1,
		);
	});

	watchEffect(() => {
		data.value.maximaleHypotheek.woonquoteBox3 = isPercentage(
			computedValues.value.woonquoteBox3,
		);
	});

	watchEffect(() => {
		data.value.maximaleHypotheek.somVanLeningdelen = asCur(
			mortgagePartsTotal.value.hoogte,
		);
	});

	watchEffect(() => {
		data.value.maximaleHypotheek.toegestaneMaandlast =
			computedValues.value.toegestaneMaandlast;
	});

	watchEffect(() => {
		data.value.maximaleHypotheek.maximaleLooptijd =
			computedValues.value.maximaleLooptijd;
	});

	watchEffect(() => {
		data.value.maximaleHypotheek.toetsAnnuiteitenFactor =
			computedValues.value.toetsAnnuiteitenFactor;
	});

	watchEffect(() => {
		data.value.maximaleHypotheek.maxLeningInkomenLasten =
			computedValues.value.maxLeningInkomenLasten;
	});

	// Berekenen maximale financieringslast per jaar
	watchEffect(() => {
		const toegestaneMaandlast =
			asCur(data.value.maximaleHypotheek.toegestaneMaandlast) ?? NaN;
		const toegestaneJaarlast = toegestaneMaandlast * 12;

		if (Number.isFinite(toegestaneJaarlast)) {
			data.value.maximaleHypotheek.maximaleFinancieringslast =
				toegestaneJaarlast;
		} else {
			data.value.maximaleHypotheek.maximaleFinancieringslast = undefined;
		}
	});

	// Toon marktwaarde
	watchEffect(() => {
		const marktwaarde = asCur(dossierDataStore.data.objectData.marktwaarde);

		data.value.maximaleHypotheek.marktwaarde = marktwaarde;
	});

	// Bereken LTV (loan-to-value)
	watchEffect(() => {
		const marktwaarde = asCur(data.value.maximaleHypotheek.marktwaarde) ?? NaN;
		const totaleLening =
			asCur(data.value.maximaleHypotheek.somVanLeningdelen) ?? NaN;

		const ltv = totaleLening / marktwaarde;

		if (Number.isFinite(ltv)) {
			data.value.maximaleHypotheek.ltv = ltv;
		} else {
			data.value.maximaleHypotheek.ltv = undefined;
		}
	});

	return { schemas, state };
}
